import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Navigate } from "react-router";
import {
  ShieldCheck,
  Check,
  Ban,
  RefreshCw,
  Plus,
  UserPlus,
  UserMinus,
  Users,
  Lock,
  Globe,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../../lib/auth/useAuth";
import {
  allowlistEmail,
  amIPlatformAdmin,
  declineEmail,
  getAllowlistMode,
  grantPlatformAdmin,
  listPlatformAdmins,
  listWaitlist,
  revokePlatformAdmin,
  setAllowlistMode,
  type PlatformAdminEntry,
  type WaitlistEntry,
  type WaitlistStatus,
} from "../../lib/waitlist";

const E2E_BYPASS = import.meta.env.VITE_E2E_BYPASS_AUTH === "true";

type LoadState =
  | { kind: "loading" }
  | { kind: "denied" }
  | { kind: "ready"; entries: WaitlistEntry[] }
  | { kind: "error"; message: string };

const FILTERS: Array<{ key: "all" | WaitlistStatus; label: string }> = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "allowlisted", label: "Allowlisted" },
  { key: "declined", label: "Declined" },
];

export function AdminWaitlist() {
  const { session, loading: authLoading, user } = useAuth();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [filter, setFilter] = useState<(typeof FILTERS)[number]["key"]>("all");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [preEmail, setPreEmail] = useState("");
  const [preBusy, setPreBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [admins, setAdmins] = useState<PlatformAdminEntry[]>([]);
  const [adminsLoading, setAdminsLoading] = useState(true);
  const [grantEmail, setGrantEmail] = useState("");
  const [grantBusy, setGrantBusy] = useState(false);
  const [revokeBusy, setRevokeBusy] = useState<string | null>(null);
  const [allowlistEnabled, setAllowlistEnabled] = useState<boolean | null>(
    // Under E2E bypass, default to enabled so the toggle renders a deterministic
    // "Beta" state for the admin-UI spec.
    E2E_BYPASS ? true : null,
  );
  const [modeBusy, setModeBusy] = useState(false);

  if (!E2E_BYPASS && !authLoading && !session) {
    return <Navigate to="/login" replace />;
  }

  async function reload() {
    if (!user) return;
    setState({ kind: "loading" });
    setAdminsLoading(true);
    try {
      if (E2E_BYPASS) {
        // Demo data so the page is reviewable in the bypass suite without
        // hitting Supabase. Exactly one row per status.
        setState({
          kind: "ready",
          entries: [
            {
              id: "demo-pending",
              user_id: "00000000-0000-0000-0000-000000000001",
              email: "pending@example.com",
              status: "pending",
              source: "google",
              note: null,
              requested_at: new Date().toISOString(),
              allowlisted_at: null,
              allowlisted_by: null,
            },
            {
              id: "demo-allowlisted",
              user_id: "00000000-0000-0000-0000-000000000002",
              email: "allowlisted@example.com",
              status: "allowlisted",
              source: "email",
              note: null,
              requested_at: new Date(Date.now() - 86400_000).toISOString(),
              allowlisted_at: new Date().toISOString(),
              allowlisted_by: null,
            },
          ],
        });
        // Demo admins: one bootstrap (non-revocable), one granted by the
        // current user (revocable), one granted by someone else (locked).
        setAdmins([
          {
            user_id: "00000000-0000-0000-0000-000000000000",
            email: "founder@example.com",
            created_at: new Date(Date.now() - 30 * 86400_000).toISOString(),
            note: "bootstrap",
            granted_by: null,
            granted_by_email: null,
            can_revoke: false,
          },
          {
            user_id: "00000000-0000-0000-0000-00000000000a",
            email: "you-granted@example.com",
            created_at: new Date(Date.now() - 3 * 86400_000).toISOString(),
            note: "granted via admin UI",
            granted_by: "00000000-0000-0000-0000-000000000000",
            granted_by_email: "you@example.com",
            can_revoke: true,
          },
          {
            user_id: "00000000-0000-0000-0000-00000000000b",
            email: "peer-granted@example.com",
            created_at: new Date(Date.now() - 2 * 86400_000).toISOString(),
            note: "granted via admin UI",
            granted_by: "00000000-0000-0000-0000-0000000000aa",
            granted_by_email: "other-founder@example.com",
            can_revoke: false,
          },
        ]);
        setAdminsLoading(false);
        return;
      }

      const admin = await amIPlatformAdmin(user.id);
      if (!admin) {
        setState({ kind: "denied" });
        setAdminsLoading(false);
        return;
      }
      const [entries, adminList, mode] = await Promise.all([
        listWaitlist(),
        listPlatformAdmins(),
        getAllowlistMode(),
      ]);
      setState({ kind: "ready", entries });
      setAdmins(adminList);
      setAllowlistEnabled(mode);
      setAdminsLoading(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load";
      setState({ kind: "error", message });
      setAdminsLoading(false);
    }
  }

  useEffect(() => {
    if (authLoading) return;
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.id]);

  const entries = state.kind === "ready" ? state.entries : [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries.filter((e) => {
      if (filter !== "all" && e.status !== filter) return false;
      if (q && !e.email.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [entries, filter, search]);

  const counts = useMemo(() => {
    const c = { pending: 0, allowlisted: 0, declined: 0 };
    for (const e of entries) c[e.status] += 1;
    return c;
  }, [entries]);

  async function handleAllowlist(entry: WaitlistEntry) {
    setBusy(entry.id);
    try {
      await allowlistEmail(entry.email);
      setToast(`Allowlisted ${entry.email}`);
      await reload();
    } catch (err) {
      setToast(err instanceof Error ? err.message : "Failed");
    } finally {
      setBusy(null);
    }
  }

  async function handleDecline(entry: WaitlistEntry) {
    setBusy(entry.id);
    try {
      await declineEmail(entry.email);
      setToast(`Declined ${entry.email}`);
      await reload();
    } catch (err) {
      setToast(err instanceof Error ? err.message : "Failed");
    } finally {
      setBusy(null);
    }
  }

  async function handlePreAllowlist(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const email = preEmail.trim();
    if (!email) return;
    setPreBusy(true);
    try {
      await allowlistEmail(email);
      setPreEmail("");
      setToast(`Allowlisted ${email}`);
      await reload();
    } catch (err) {
      setToast(err instanceof Error ? err.message : "Failed");
    } finally {
      setPreBusy(false);
    }
  }

  async function handleGrantAdmin(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const email = grantEmail.trim();
    if (!email) return;
    setGrantBusy(true);
    try {
      await grantPlatformAdmin(email);
      setGrantEmail("");
      setToast(`Granted admin to ${email}`);
      await reload();
    } catch (err) {
      setToast(err instanceof Error ? err.message : "Failed");
    } finally {
      setGrantBusy(false);
    }
  }

  async function handleRevokeAdmin(entry: PlatformAdminEntry) {
    if (!entry.can_revoke) return;
    const label = entry.email ?? entry.user_id;
    if (!window.confirm(`Revoke admin access for ${label}?`)) return;
    setRevokeBusy(entry.user_id);
    try {
      await revokePlatformAdmin(entry.user_id);
      setToast(`Revoked admin from ${label}`);
      await reload();
    } catch (err) {
      setToast(err instanceof Error ? err.message : "Failed");
    } finally {
      setRevokeBusy(null);
    }
  }

  async function handleToggleMode(next: boolean) {
    if (allowlistEnabled === next) return;
    const message = next
      ? "Re-enable the allowlist? Users who signed up while it was off will lose access until you allowlist them."
      : "Turn the allowlist OFF? Any authenticated user will be able to enter Proofly, bypassing the waitlist.";
    if (!window.confirm(message)) return;
    setModeBusy(true);
    try {
      if (E2E_BYPASS) {
        // In bypass mode we only flip local state; no Supabase round-trip.
        setAllowlistEnabled(next);
      } else {
        const applied = await setAllowlistMode(next);
        setAllowlistEnabled(applied);
      }
      setToast(
        next ? "Allowlist re-enabled (Beta)" : "Allowlist turned off (Open)",
      );
    } catch (err) {
      setToast(err instanceof Error ? err.message : "Failed to toggle");
    } finally {
      setModeBusy(false);
    }
  }

  if (state.kind === "denied") {
    return (
      <div className="max-w-md mx-auto mt-12 rounded-xl border border-slate-200 bg-white p-6 text-center">
        <h1 className="text-lg font-semibold text-slate-900 mb-1">
          Not authorized
        </h1>
        <p className="text-sm text-slate-600">
          This page is restricted to platform admins.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
        <header className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-[#0070F2]" />
            <div>
              <h1 className="text-lg font-semibold text-slate-900">
                Alpha waitlist
              </h1>
              <p className="text-sm text-slate-500">
                {counts.pending} pending · {counts.allowlisted} allowlisted ·{" "}
                {counts.declined} declined
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ModeToggle
              enabled={allowlistEnabled}
              busy={modeBusy}
              onChange={handleToggleMode}
            />
            <Button
              type="button"
              variant="ghost"
              onClick={reload}
              disabled={state.kind === "loading"}
              data-testid="admin-waitlist-refresh"
              className="gap-2"
            >
              <RefreshCw
                className={`w-4 h-4 ${state.kind === "loading" ? "animate-spin" : ""}`}
              />
              Refresh
            </Button>
          </div>
        </header>

        <section
          data-testid="admin-platform-admins-section"
          className="mb-6 rounded-xl border border-slate-200 bg-white p-4"
        >
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-slate-500" />
            <h2 className="text-sm font-semibold text-slate-900">
              Platform admins
            </h2>
            <span className="text-xs text-slate-500">
              ({admins.length} total)
            </span>
          </div>

          <form
            onSubmit={handleGrantAdmin}
            className="flex items-center gap-2 mb-4"
          >
            <Input
              type="email"
              placeholder="grant admin to an existing user…"
              value={grantEmail}
              onChange={(e) => setGrantEmail(e.target.value)}
              disabled={grantBusy}
              data-testid="admin-grant-email"
              className="max-w-xs"
            />
            <Button
              type="submit"
              disabled={grantBusy || !grantEmail.trim()}
              data-testid="admin-grant-submit"
              className="gap-2 bg-slate-900 hover:bg-slate-800 text-white"
            >
              <UserPlus className="w-4 h-4" />
              {grantBusy ? "Granting…" : "Grant admin"}
            </Button>
          </form>

          {adminsLoading ? (
            <div className="py-6 text-center text-sm text-slate-500">
              Loading…
            </div>
          ) : admins.length === 0 ? (
            <div className="py-6 text-center text-sm text-slate-500">
              No platform admins yet.
            </div>
          ) : (
            <div className="overflow-hidden rounded-md border border-slate-100">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2 font-medium">Admin</th>
                    <th className="px-4 py-2 font-medium">Granted by</th>
                    <th className="px-4 py-2 font-medium">Since</th>
                    <th className="px-4 py-2 font-medium text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {admins.map((a) => {
                    const label = a.email ?? a.user_id;
                    const lockedReason = a.granted_by
                      ? "Only the admin who granted this can revoke it."
                      : "Bootstrap admin — remove via SQL editor only.";
                    return (
                      <tr
                        key={a.user_id}
                        data-testid={`admin-row-${label}`}
                        className="border-t border-slate-100"
                      >
                        <td className="px-4 py-3 font-medium text-slate-900">
                          {label}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {a.granted_by_email ?? (
                            <span className="italic text-slate-400">
                              bootstrap
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {formatRelative(a.created_at)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRevokeAdmin(a)}
                            disabled={
                              !a.can_revoke || revokeBusy === a.user_id
                            }
                            title={a.can_revoke ? undefined : lockedReason}
                            data-testid={`admin-revoke-${label}`}
                            className="gap-1 text-slate-500 hover:text-red-600 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-slate-500"
                          >
                            <UserMinus className="w-3.5 h-3.5" />
                            {revokeBusy === a.user_id ? "Revoking…" : "Revoke"}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="mb-4 rounded-xl border border-slate-200 bg-white p-4">
          <form
            onSubmit={handlePreAllowlist}
            className="flex items-center gap-2"
          >
            <Input
              type="email"
              placeholder="pre-allowlist an email…"
              value={preEmail}
              onChange={(e) => setPreEmail(e.target.value)}
              disabled={preBusy}
              data-testid="admin-waitlist-pre-email"
              className="max-w-xs"
            />
            <Button
              type="submit"
              disabled={preBusy || !preEmail.trim()}
              data-testid="admin-waitlist-pre-submit"
              className="gap-2 bg-[#0070F2] hover:bg-[#005BCA] text-white"
            >
              <Plus className="w-4 h-4" />
              {preBusy ? "Adding…" : "Allowlist"}
            </Button>
          </form>
        </section>

        <div className="mb-3 flex items-center gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              data-testid={`admin-waitlist-filter-${f.key}`}
              className={`px-3 py-1 rounded-full text-sm border ${
                filter === f.key
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
              }`}
            >
              {f.label}
            </button>
          ))}
          <Input
            type="search"
            placeholder="Search email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-xs ml-auto"
          />
        </div>

        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          {state.kind === "loading" && (
            <div className="p-8 text-center text-sm text-slate-500">
              Loading…
            </div>
          )}
          {state.kind === "error" && (
            <div className="p-8 text-center text-sm text-red-600">
              {state.message}
            </div>
          )}
          {state.kind === "ready" && filtered.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-500">
              No waitlist entries match.
            </div>
          )}
          {state.kind === "ready" && filtered.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Email</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Source</th>
                  <th className="px-4 py-2 font-medium">Requested</th>
                  <th className="px-4 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((entry) => (
                  <tr
                    key={entry.id}
                    data-testid={`admin-waitlist-row-${entry.email}`}
                    className="border-t border-slate-100"
                  >
                    <td className="px-4 py-3 font-medium text-slate-900">
                      {entry.email}
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill status={entry.status} />
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {entry.source ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {formatRelative(entry.requested_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        {entry.status !== "allowlisted" && (
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => handleAllowlist(entry)}
                            disabled={busy === entry.id}
                            data-testid={`admin-waitlist-allow-${entry.email}`}
                            className="gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                          >
                            <Check className="w-3.5 h-3.5" /> Allow
                          </Button>
                        )}
                        {entry.status !== "declined" && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDecline(entry)}
                            disabled={busy === entry.id}
                            data-testid={`admin-waitlist-decline-${entry.email}`}
                            className="gap-1 text-slate-500 hover:text-red-600"
                          >
                            <Ban className="w-3.5 h-3.5" /> Decline
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {toast && (
          <div
            role="status"
            className="fixed bottom-4 right-4 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm shadow-sm"
            onAnimationEnd={() => setToast(null)}
          >
            {toast}
          </div>
        )}
    </div>
  );
}

function StatusPill({ status }: { status: WaitlistStatus }) {
  const cls =
    status === "allowlisted"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : status === "declined"
        ? "bg-red-50 text-red-700 border-red-200"
        : "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${cls}`}
    >
      {status}
    </span>
  );
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

// Segmented toggle: ON = "Beta (allowlist on)", OFF = "Open (anyone signed in)".
// `enabled === null` while the initial fetch is in flight.
function ModeToggle({
  enabled,
  busy,
  onChange,
}: {
  enabled: boolean | null;
  busy: boolean;
  onChange: (next: boolean) => void;
}) {
  const disabled = enabled === null || busy;
  return (
    <div
      data-testid="admin-mode-toggle"
      data-mode={enabled === null ? "loading" : enabled ? "beta" : "open"}
      className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 p-0.5 text-xs font-medium"
    >
      <button
        type="button"
        data-testid="admin-mode-beta"
        aria-pressed={enabled === true}
        disabled={disabled}
        onClick={() => onChange(true)}
        className={`inline-flex items-center gap-1 rounded-full px-3 py-1 transition-colors ${
          enabled === true
            ? "bg-[#0070F2] text-white shadow-sm"
            : "text-slate-600 hover:text-slate-900"
        } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <Lock className="w-3.5 h-3.5" />
        Beta
      </button>
      <button
        type="button"
        data-testid="admin-mode-open"
        aria-pressed={enabled === false}
        disabled={disabled}
        onClick={() => onChange(false)}
        className={`inline-flex items-center gap-1 rounded-full px-3 py-1 transition-colors ${
          enabled === false
            ? "bg-emerald-600 text-white shadow-sm"
            : "text-slate-600 hover:text-slate-900"
        } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <Globe className="w-3.5 h-3.5" />
        Open
      </button>
    </div>
  );
}
